import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Monitor,
  Shield,
  Layers,
  FileText,
  Search,
  Lock,
  Sparkles,
} from 'lucide-react';
import { useAppConfig } from '@state';
import { STUDY_LIST_PATH } from '../../constants/routes';
import publicUrl from '../../utils/publicUrl';
import './HomePage.css';

const navItemClass =
  'text-sm font-medium text-slate-600 transition-colors hover:text-indigo-600';

function HomePage() {
  const [appConfig] = useAppConfig();
  const { showStudyList } = appConfig;
  const mprHeroSrc = `${String(publicUrl).replace(/\/?$/, '/')}assets/homepage-mpr.png?v=2`;
  const primaryStart = showStudyList ? STUDY_LIST_PATH : '/local';

  useEffect(() => {
    const id = 'radkspace-home-fonts';
    if (!document.getElementById(id)) {
      const link = document.createElement('link');
      link.id = id;
      link.rel = 'stylesheet';
      link.href =
        'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className="paper-home min-h-screen bg-white text-slate-900 antialiased selection:bg-indigo-100">
      {/* Top navigation only — Paper Library–style */}
      <header className="sticky top-0 z-50 border-b border-slate-200/90 bg-white/95 backdrop-blur-md">
        <nav className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-4 sm:px-6">
          <Link
            to="/"
            className="flex items-center gap-2.5 text-lg font-semibold tracking-tight text-slate-900"
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white shadow-sm">
              <Monitor className="h-5 w-5" aria-hidden />
            </span>
            Radkspace Viewer
          </Link>

          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2 sm:gap-x-8">
            <a
              href="#features"
              className={navItemClass}
            >
              Features
            </a>
            <a
              href="#about"
              className={navItemClass}
            >
              About
            </a>
            <a
              href="#pricing"
              className={navItemClass}
            >
              Pricing
            </a>
            <a
              href="#"
              className={navItemClass}
              onClick={e => e.preventDefault()}
            >
              Sign in
            </a>
            <Link
              to={primaryStart}
              className="rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500"
            >
              Get started
            </Link>
          </div>
        </nav>
      </header>

      <main>
        {/* Hero — clear headline + dual CTAs */}
        <section className="border-b border-slate-100 bg-gradient-to-b from-indigo-50/40 to-white px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-slate-900 sm:text-5xl lg:text-[3.25rem] lg:leading-[1.1]">
              Read, report, and search DICOM — in one calm workspace.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              A modern viewer for radiology workflows: fast local loading, MPR where you need it, and
              drafting next to the stack. Built for clarity — minimal chrome, indigo accents, and your
              data staying on-device when you choose local sources.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to={primaryStart}
                className="inline-flex w-full min-w-[200px] items-center justify-center rounded-lg bg-indigo-600 px-8 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500 sm:w-auto"
              >
                Get started free
              </Link>
              <a
                href="#pricing"
                className="inline-flex w-full min-w-[200px] items-center justify-center rounded-lg border border-slate-300 bg-white px-8 py-3.5 text-base font-semibold text-slate-800 shadow-sm transition-colors hover:border-indigo-300 hover:text-indigo-700 sm:w-auto"
              >
                View pricing
              </a>
            </div>
          </div>
        </section>

        {/* Product preview */}
        <section className="px-4 py-14 sm:px-6 sm:py-16">
          <div className="mx-auto max-w-5xl">
            <p className="mb-4 text-center text-sm font-medium uppercase tracking-wider text-indigo-600">
              Product preview
            </p>
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-xl shadow-slate-200/60 ring-1 ring-slate-100">
              <img
                src={mprHeroSrc}
                alt="Radkspace Viewer — MPR with integrated reporting pane"
                className="block h-auto w-full object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </section>

        {/* Features */}
        <section
          id="features"
          className="scroll-mt-20 border-t border-slate-100 bg-slate-50/80 px-4 py-16 sm:px-6 sm:py-20"
        >
          <div className="mx-auto max-w-6xl">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                Everything you need at the console
              </h2>
              <p className="mt-4 text-lg text-slate-600">
                Organised like a product site — fewer distractions, faster paths to the study list and
                local upload.
              </p>
            </div>

            <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: Layers,
                  title: 'Multi-planar review',
                  body: 'Axial, sagittal, and coronal layouts with familiar window/level tools — tuned for diagnostic pace.',
                },
                {
                  icon: FileText,
                  title: 'Reporting beside the stack',
                  body: 'Draft findings alongside images so context stays in view — export when your workflow allows.',
                },
                {
                  icon: Search,
                  title: 'Study discovery',
                  body: 'Filter and open studies from your configured sources; keep uploads folder-based when working locally.',
                },
                {
                  icon: Shield,
                  title: 'Privacy-conscious by design',
                  body: 'Prefer local DICOM and controlled endpoints; you decide what leaves the workstation.',
                },
                {
                  icon: Lock,
                  title: 'Sign-in ready',
                  body: 'Wire your OIDC / enterprise auth through standard OHIF configuration when you deploy.',
                },
                {
                  icon: Sparkles,
                  title: 'Indigo-first UI',
                  body: 'A single accent color for actions and focus — consistent with a calm reading room.',
                },
              ].map(({ icon: Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{body}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/local"
                className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
              >
                Upload DICOM from this device →
              </Link>
              {showStudyList ? (
                <>
                  <span className="mx-2 text-slate-300">|</span>
                  <Link
                    to={STUDY_LIST_PATH}
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Open study list →
                  </Link>
                </>
              ) : null}
            </div>
          </div>
        </section>

        {/* About */}
        <section
          id="about"
          className="scroll-mt-20 px-4 py-16 sm:px-6 sm:py-20"
        >
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">About Radkspace Viewer</h2>
            <p className="mt-6 text-lg leading-relaxed text-slate-600">
              Radkspace Viewer is built on the open{' '}
              <a
                href="https://ohif.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:underline"
              >
                OHIF
              </a>{' '}
              platform. This fork emphasises a clear marketing home, local-first messaging, and routes for
              upload, settings, and the clinical viewer — without extra navigation chrome on the landing
              page.
            </p>
            <Link
              to="/settings"
              className="mt-8 inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Application settings →
            </Link>
          </div>
        </section>

        {/* Pricing placeholder — Paper Library style clarity */}
        <section
          id="pricing"
          className="scroll-mt-20 border-t border-slate-100 bg-slate-50/80 px-4 py-16 sm:px-6 sm:py-20"
        >
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold text-slate-900 sm:text-4xl">Pricing</h2>
            <p className="mt-4 text-lg text-slate-600">
              Self-hosted and fork-friendly. Replace this block with your plans, trials, or enterprise
              contact — same layout as{' '}
              <a
                href="https://paperlibrary.org/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:underline"
              >
                Paper Library
              </a>
              &apos;s pricing section when you are ready.
            </p>
            <div className="mt-10 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
              <p className="text-sm font-medium text-indigo-600">Starter</p>
              <p className="mt-2 text-3xl font-bold text-slate-900">Free to evaluate</p>
              <p className="mt-2 text-slate-600">Run locally, connect your PACS or static demo data.</p>
              <Link
                to={primaryStart}
                className="mt-6 inline-flex rounded-lg bg-indigo-600 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-500"
              >
                Open the viewer
              </Link>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-indigo-600 px-4 py-16 text-center sm:px-6 sm:py-20">
          <div className="mx-auto max-w-2xl">
            <h2 className="text-3xl font-bold text-white sm:text-4xl">Ready to open your next study?</h2>
            <p className="mt-4 text-lg text-indigo-100">
              Start from the study list or upload a folder — the top bar stays minimal everywhere else.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
              <Link
                to={primaryStart}
                className="inline-flex w-full min-w-[200px] justify-center rounded-lg bg-white px-8 py-3.5 text-base font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 sm:w-auto"
              >
                Create session
              </Link>
              <Link
                to="/local"
                className="inline-flex w-full min-w-[200px] justify-center rounded-lg border border-indigo-400/60 bg-transparent px-8 py-3.5 text-base font-semibold text-white hover:bg-indigo-500/30 sm:w-auto"
              >
                Upload DICOM
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-slate-50 px-4 py-12 sm:px-6">
        <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2 font-semibold text-slate-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-600 text-white">
                <Monitor className="h-4 w-4" aria-hidden />
              </span>
              Radkspace Viewer
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">
              DICOM in the browser with a clear home page and OHIF-powered viewing.
            </p>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Product</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href="#features"
                  className="text-slate-700 hover:text-indigo-600"
                >
                  Features
                </a>
              </li>
              <li>
                <Link
                  to="/local"
                  className="text-slate-700 hover:text-indigo-600"
                >
                  Upload
                </Link>
              </li>
              {showStudyList ? (
                <li>
                  <Link
                    to={STUDY_LIST_PATH}
                    className="text-slate-700 hover:text-indigo-600"
                  >
                    Study list
                  </Link>
                </li>
              ) : null}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Company</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href="#about"
                  className="text-slate-700 hover:text-indigo-600"
                >
                  About
                </a>
              </li>
              <li>
                <Link
                  to="/settings"
                  className="text-slate-700 hover:text-indigo-600"
                >
                  Settings
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Legal</h3>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <a
                  href="#"
                  className="text-slate-700 hover:text-indigo-600"
                  onClick={e => e.preventDefault()}
                >
                  Privacy
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-slate-700 hover:text-indigo-600"
                  onClick={e => e.preventDefault()}
                >
                  Terms
                </a>
              </li>
            </ul>
          </div>
        </div>
        <p className="mx-auto mt-10 max-w-6xl border-t border-slate-200 pt-8 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} Radkspace Viewer · Based on{' '}
          <a
            href="https://ohif.org/"
            className="text-indigo-600 hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            OHIF
          </a>
        </p>
      </footer>
    </div>
  );
}

export default HomePage;
